<script setup lang="ts">
import { Check, ChevronDown } from "lucide-vue-next"
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useId,
  watch,
} from "vue"

export interface AppSelectOption {
  value: string
  label: string
  disabled?: boolean
}

const props = withDefaults(
  defineProps<{
    modelValue: string
    options: AppSelectOption[]
    ariaLabel?: string
    disabled?: boolean
    placeholder?: string
    compact?: boolean
  }>(),
  {
    ariaLabel: "请选择",
    disabled: false,
    placeholder: "请选择",
    compact: false,
  },
)

const emit = defineEmits<{
  "update:modelValue": [value: string]
  change: [value: string]
}>()

const root = ref<HTMLElement | null>(null)
const trigger = ref<HTMLButtonElement | null>(null)
const menu = ref<HTMLElement | null>(null)
const open = ref(false)
const activeIndex = ref(-1)
const menuStyle = ref<Record<string, string>>({})
const listboxId = `app-select-${useId()}`

const selectedIndex = computed(() =>
  props.options.findIndex((option) => option.value === props.modelValue),
)
const selectedOption = computed(() => props.options[selectedIndex.value])

watch(
  () => props.modelValue,
  () => {
    activeIndex.value = selectedIndex.value
  },
)

function updatePosition(): void {
  const element = trigger.value
  if (!element) return
  const rect = element.getBoundingClientRect()
  const estimatedHeight = Math.min(props.options.length * 40 + 12, 248)
  const spaceBelow = window.innerHeight - rect.bottom - 12
  const openAbove = spaceBelow < estimatedHeight && rect.top > spaceBelow
  menuStyle.value = {
    left: `${Math.max(10, Math.min(rect.left, window.innerWidth - rect.width - 10))}px`,
    top: openAbove
      ? `${Math.max(10, rect.top - estimatedHeight - 7)}px`
      : `${rect.bottom + 7}px`,
    width: `${rect.width}px`,
  }
}

async function openMenu(preferredIndex = selectedIndex.value): Promise<void> {
  if (props.disabled || props.options.length === 0) return
  activeIndex.value = preferredIndex >= 0 ? preferredIndex : firstEnabledIndex()
  open.value = true
  updatePosition()
  await nextTick()
  menu.value
    ?.querySelector<HTMLElement>(`[data-index="${activeIndex.value}"]`)
    ?.scrollIntoView({ block: "nearest" })
}

function closeMenu(restoreFocus = false): void {
  open.value = false
  if (restoreFocus) nextTick(() => trigger.value?.focus())
}

function toggleMenu(): void {
  if (open.value) closeMenu()
  else void openMenu()
}

function firstEnabledIndex(): number {
  return props.options.findIndex((option) => !option.disabled)
}

function lastEnabledIndex(): number {
  for (let index = props.options.length - 1; index >= 0; index -= 1) {
    if (!props.options[index]?.disabled) return index
  }
  return -1
}

function moveActive(step: 1 | -1): void {
  if (!open.value) {
    void openMenu(step === 1 ? firstEnabledIndex() : lastEnabledIndex())
    return
  }
  let next = activeIndex.value
  for (let count = 0; count < props.options.length; count += 1) {
    next = (next + step + props.options.length) % props.options.length
    if (!props.options[next]?.disabled) {
      activeIndex.value = next
      nextTick(() =>
        menu.value
          ?.querySelector<HTMLElement>(`[data-index="${next}"]`)
          ?.scrollIntoView({ block: "nearest" }),
      )
      return
    }
  }
}

function choose(option: AppSelectOption): void {
  if (option.disabled) return
  emit("update:modelValue", option.value)
  emit("change", option.value)
  closeMenu(true)
}

function onTriggerKeydown(event: KeyboardEvent): void {
  if (event.key === "ArrowDown") {
    event.preventDefault()
    moveActive(1)
  } else if (event.key === "ArrowUp") {
    event.preventDefault()
    moveActive(-1)
  } else if (event.key === "Enter" || event.key === " ") {
    event.preventDefault()
    if (open.value && activeIndex.value >= 0) {
      const option = props.options[activeIndex.value]
      if (option) choose(option)
    } else {
      void openMenu()
    }
  } else if (event.key === "Escape" && open.value) {
    event.preventDefault()
    closeMenu()
  } else if (event.key === "Home" && open.value) {
    event.preventDefault()
    activeIndex.value = firstEnabledIndex()
  } else if (event.key === "End" && open.value) {
    event.preventDefault()
    activeIndex.value = lastEnabledIndex()
  }
}

function onDocumentPointerDown(event: PointerEvent): void {
  const target = event.target as Node
  if (!root.value?.contains(target) && !menu.value?.contains(target)) closeMenu()
}

function onViewportChange(): void {
  if (open.value) updatePosition()
}

onMounted(() => {
  document.addEventListener("pointerdown", onDocumentPointerDown, true)
  window.addEventListener("resize", onViewportChange)
  window.addEventListener("scroll", onViewportChange, true)
})

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", onDocumentPointerDown, true)
  window.removeEventListener("resize", onViewportChange)
  window.removeEventListener("scroll", onViewportChange, true)
})
</script>

<template>
  <div ref="root" class="app-select" :class="{ 'app-select--compact': compact, 'app-select--open': open }">
    <button
      ref="trigger"
      type="button"
      class="app-select__trigger"
      role="combobox"
      :aria-label="ariaLabel"
      :aria-controls="listboxId"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :disabled="disabled"
      @click="toggleMenu"
      @keydown="onTriggerKeydown"
    >
      <span :class="{ 'app-select__placeholder': !selectedOption }">{{ selectedOption?.label ?? placeholder }}</span>
      <ChevronDown class="app-select__chevron" :size="15" />
    </button>

    <Teleport to="body">
      <Transition name="app-select-menu">
        <div
          v-if="open"
          :id="listboxId"
          ref="menu"
          class="app-select__menu"
          role="listbox"
          :style="menuStyle"
          :aria-label="ariaLabel"
          @keydown.esc.prevent="closeMenu(true)"
        >
          <button
            v-for="(option, index) in options"
            :key="String(option.value)"
            type="button"
            class="app-select__option"
            :class="{
              'app-select__option--active': index === activeIndex,
              'app-select__option--selected': option.value === modelValue,
            }"
            role="option"
            :aria-selected="option.value === modelValue"
            :disabled="option.disabled"
            :data-index="index"
            @mouseenter="activeIndex = index"
            @click="choose(option)"
          >
            <span>{{ option.label }}</span>
            <Check v-if="option.value === modelValue" :size="14" />
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.app-select { position: relative; width: 100%; min-width: 0; }
.app-select__trigger {
  width: 100%;
  min-height: 43px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 12px 0 13px;
  border: 1px solid var(--line-strong);
  border-radius: 9px;
  color: var(--ink);
  background: var(--panel);
  cursor: pointer;
  outline: 0;
  text-align: left;
  transition: border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease;
}
.app-select__trigger:hover:not(:disabled) { border-color: color-mix(in oklch, var(--ink) 52%, var(--line-strong)); background: color-mix(in oklch, var(--panel) 88%, white); }
.app-select__trigger:focus-visible, .app-select--open .app-select__trigger { border-color: var(--accent); box-shadow: 0 0 0 3px color-mix(in oklch, var(--accent) 13%, transparent); }
.app-select__trigger:disabled { cursor: not-allowed; opacity: .58; }
.app-select__trigger > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.app-select__placeholder { color: var(--faint); }
.app-select__chevron { flex: 0 0 auto; color: var(--muted); transition: transform 150ms ease; }
.app-select--open .app-select__chevron { transform: rotate(180deg); }
.app-select--compact .app-select__trigger { min-height: 40px; font-size: 11px; }
.app-select.admin-select { width: auto; min-width: 140px; }

.app-select__menu {
  position: fixed;
  z-index: 240;
  max-height: 248px;
  padding: 6px;
  overflow-y: auto;
  border: 1px solid var(--line-strong);
  border-radius: 11px;
  background: var(--panel);
  box-shadow: 0 18px 50px color-mix(in oklch, var(--ink) 20%, transparent), 0 2px 8px color-mix(in oklch, var(--ink) 10%, transparent);
}
.app-select__option {
  width: 100%;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 10px;
  border-radius: 7px;
  color: var(--ink-soft);
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  text-align: left;
}
.app-select__option span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.app-select__option svg { flex: 0 0 auto; color: var(--accent); }
.app-select__option--active { color: var(--ink); background: var(--panel-muted); }
.app-select__option--selected { color: var(--accent); font-weight: 650; }
.app-select__option:disabled { cursor: not-allowed; opacity: .45; }
.app-select-menu-enter-active, .app-select-menu-leave-active { transition: opacity 120ms ease, transform 120ms ease; transform-origin: top; }
.app-select-menu-enter-from, .app-select-menu-leave-to { opacity: 0; transform: translateY(-4px) scale(.985); }
</style>
